// Shapefile reader. shpjs parses each record's geometry and the DBF rows.
// The file-level parser keys off the header shape type and stops at the first
// truncated record, so each record is handed to shpjs on its own. A missing or
// unusable .prj is refused rather than drawn in the wrong CRS.
import proj4 from "proj4";
import { parseDbf, parseShp } from "shpjs";

type XY = [number, number];

interface FieldDesc {
  name: string;
  type: string;
  length: number;
}

function done(warns: string[], features: GeoJSON.Feature[]): GeoJSON.Feature[] {
  if (warns.length) console.warn(`readShapefile: ${warns.join("; ")}`);
  return features;
}

function signedArea2(ring: XY[]): number {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    sum += ring[i]![0] * ring[i + 1]![1] - ring[i + 1]![0] * ring[i]![1];
  }
  return sum;
}

function pointInRing(pt: XY, ring: XY[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (
      yi > pt[1] !== yj > pt[1] &&
      pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

function withWinding(ring: XY[], want: "ccw" | "cw"): XY[] {
  const ccw = signedArea2(ring) > 0;
  return (want === "ccw") === ccw ? ring.slice() : ring.slice().reverse();
}

// shpjs groups rings by winding and bounding box. A same-winding hole becomes a
// second filled polygon, and a ring in a concave notch is punched as a hole.
// Containment is the part count: even depth is an outer, odd depth is a hole.
function assemblePolygon(rings: XY[][]): GeoJSON.Geometry | "skip" {
  const closed = rings.filter((ring) => ring.length >= 4);
  if (!closed.length) return "skip";
  const depth = closed.map((ring, i) =>
    closed.reduce(
      (n, other, j) => (j !== i && pointInRing(ring[0]!, other) ? n + 1 : n),
      0,
    ),
  );
  const polys: XY[][][] = [];
  const polyOfOuter = new Map<number, XY[][]>();
  closed.forEach((ring, i) => {
    if (depth[i]! % 2 === 0) {
      const poly: XY[][] = [withWinding(ring, "ccw")];
      polyOfOuter.set(i, poly);
      polys.push(poly);
    }
  });
  closed.forEach((ring, i) => {
    if (depth[i]! % 2 === 0) return;
    let parent = -1;
    let best = -1;
    for (const j of polyOfOuter.keys()) {
      if (pointInRing(ring[0]!, closed[j]!) && depth[j]! > best) {
        best = depth[j]!;
        parent = j;
      }
    }
    if (parent >= 0) polyOfOuter.get(parent)!.push(withWinding(ring, "cw"));
    else polys.push([withWinding(ring, "ccw")]);
  });
  return polys.length === 1
    ? { type: "Polygon", coordinates: polys[0]! }
    : { type: "MultiPolygon", coordinates: polys };
}

function xy(position: number[]): XY {
  return [position[0] ?? 0, position[1] ?? 0];
}

function ringsOf(geometry: GeoJSON.Geometry): XY[][] {
  if (geometry.type === "Polygon")
    return geometry.coordinates.map((ring) => ring.map(xy));
  if (geometry.type === "MultiPolygon")
    return geometry.coordinates.flatMap((poly) =>
      poly.map((ring) => ring.map(xy)),
    );
  return [];
}

function linesOf(geometry: GeoJSON.Geometry): XY[][] {
  if (geometry.type === "LineString") return [geometry.coordinates.map(xy)];
  if (geometry.type === "MultiLineString")
    return geometry.coordinates.map((line) => line.map(xy));
  return [];
}

function acceptGeometry(geometry: GeoJSON.Geometry): GeoJSON.Geometry {
  if (geometry.type === "Point")
    return { type: "Point", coordinates: xy(geometry.coordinates) };
  if (geometry.type === "MultiPoint")
    return { type: "MultiPoint", coordinates: geometry.coordinates.map(xy) };
  const lines = linesOf(geometry).filter((line) => line.length >= 2);
  if (lines.length === 1) return { type: "LineString", coordinates: lines[0]! };
  if (lines.length > 1) return { type: "MultiLineString", coordinates: lines };
  const assembled = assemblePolygon(ringsOf(geometry));
  if (assembled === "skip") throw new Error("degenerate geometry");
  return assembled;
}

// Polygon and polyline records share a body. shpjs's polygon path drops a ring
// whose box is not inside an outer box, even when the ring's first vertex is
// inside that outer. Reading the parts as lines keeps every ring for the
// containment pass below.
function polygonType(type: number): boolean {
  // M-types sit 20 above the base, Z-types 10 above. PolygonZ is 15.
  const base = type > 20 ? type - 20 : type > 10 ? type - 10 : type;
  return base === 5;
}

function acceptRings(geometry: GeoJSON.Geometry): GeoJSON.Geometry {
  const rings =
    geometry.type === "LineString"
      ? [geometry.coordinates.map(xy)]
      : geometry.type === "MultiLineString"
        ? geometry.coordinates.map((line) => line.map(xy))
        : ringsOf(geometry);
  const assembled = assemblePolygon(rings);
  if (assembled === "skip") throw new Error("degenerate geometry");
  return assembled;
}

function parseRecord(content: Uint8Array): GeoJSON.Geometry {
  const type = new DataView(content.buffer, content.byteOffset, 4).getInt32(
    0,
    true,
  );
  const total = 100 + 8 + content.byteLength;
  const file = new Uint8Array(total);
  const view = new DataView(file.buffer);
  view.setInt32(0, 9994, false);
  view.setInt32(24, total / 2, false);
  view.setInt32(28, 1000, true);
  view.setInt32(32, polygonType(type) ? 3 : type, true);
  view.setInt32(100, 1, false);
  view.setInt32(104, content.byteLength / 2, false);
  file.set(content, 108);
  const geometry = parseShp(file)[0];
  if (!geometry) throw new Error("empty shape");
  return polygonType(type) ? acceptRings(geometry) : acceptGeometry(geometry);
}

function geometries(
  shp: Uint8Array,
  warns: string[],
): (GeoJSON.Geometry | null)[] {
  if (shp.byteLength < 100) {
    warns.push(".shp shorter than a header — file dropped");
    return [];
  }
  const view = new DataView(shp.buffer, shp.byteOffset, shp.byteLength);
  const out: (GeoJSON.Geometry | null)[] = [];
  let at = 100;
  let skipped = 0;
  while (at + 8 <= shp.byteLength) {
    const words = view.getInt32(at + 4, false);
    const contentBytes = words * 2;
    const next = at + 8 + contentBytes;
    if (contentBytes < 4 || next > shp.byteLength) {
      skipped++;
      warns.push(
        `truncated/corrupt record header at byte ${at} — stopped scanning`,
      );
      break;
    }
    const type = view.getInt32(at + 8, true);
    if (type === 0) out.push(null);
    else {
      try {
        out.push(parseRecord(shp.subarray(at + 8, next)));
      } catch {
        skipped++;
        out.push(null);
      }
    }
    at = next;
  }
  if (skipped)
    warns.push(`${skipped} malformed/unsupported .shp record(s) skipped`);
  return out;
}

function typeValue(raw: string, type: string): unknown {
  const s = raw.trim();
  switch (type) {
    case "N":
    case "F": {
      if (!s) return null;
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }
    case "L":
      if (/^[YyTt]/.test(s)) return true;
      if (/^[NnFf]/.test(s)) return false;
      return null;
    case "D":
      return /^\d{8}$/.test(s)
        ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
        : null;
    default:
      return s;
  }
}

function fieldTable(dbf: Uint8Array): {
  fields: FieldDesc[];
  headerSize: number;
  recordSize: number;
} | null {
  if (dbf.byteLength < 33) return null;
  const view = new DataView(dbf.buffer, dbf.byteOffset, dbf.byteLength);
  const headerSize = view.getUint16(8, true);
  const recordSize = view.getUint16(10, true);
  const fields: FieldDesc[] = [];
  let at = 32;
  while (
    at + 32 <= headerSize &&
    at + 32 <= dbf.byteLength &&
    dbf[at] !== 0x0d
  ) {
    let end = at;
    while (end < at + 11 && dbf[end] !== 0) end++;
    fields.push({
      name: new TextDecoder("latin1").decode(dbf.subarray(at, end)).trim(),
      type: String.fromCharCode(dbf[at + 11]!),
      length: dbf[at + 16]!,
    });
    at += 32;
  }
  if (!fields.length || recordSize < 1) return null;
  return { fields, headerSize, recordSize };
}

function dbfRows(
  dbf: Uint8Array,
  warns: string[],
): (Record<string, unknown> | null)[] {
  let parsed: Record<string, unknown>[];
  try {
    parsed = parseDbf(dbf, "windows-1252");
  } catch {
    warns.push(".dbf too short for a header — attributes dropped");
    return [];
  }
  const table = fieldTable(dbf);
  if (!table) {
    warns.push(".dbf has no readable field descriptors — attributes dropped");
    return [];
  }
  const latin1 = new TextDecoder("latin1");
  return parsed.map((row, i) => {
    const rowAt = table.headerSize + i * table.recordSize;
    if (rowAt >= dbf.byteLength) return null;
    // A deleted row keeps its slot so later rows stay on the right shape.
    if (dbf[rowAt] === 0x2a) return null;
    const props: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(row)) {
      const name = key.replaceAll("\0", "").trim();
      if (typeof value === "number")
        props[name] = Number.isFinite(value) ? value : null;
      else if (typeof value === "string") props[name] = value.trim();
      else props[name] = value;
    }
    // parsedbf collapses an uninitialised logical to false and returns dates as Date.
    let fAt = rowAt + 1;
    for (const field of table.fields) {
      if (field.type === "L" || field.type === "D") {
        props[field.name] = typeValue(
          latin1.decode(dbf.subarray(fAt, fAt + field.length)),
          field.type,
        );
      }
      fAt += field.length;
    }
    return props;
  });
}

function mapCoords(geometry: GeoJSON.Geometry, fn: (pt: XY) => XY): void {
  const one = (c: number[]): void => {
    const [x, y] = fn([c[0]!, c[1]!]);
    c[0] = x;
    c[1] = y;
  };
  if (geometry.type === "Point") one(geometry.coordinates);
  else if (geometry.type === "MultiPoint" || geometry.type === "LineString")
    geometry.coordinates.forEach(one);
  else if (geometry.type === "MultiLineString" || geometry.type === "Polygon")
    geometry.coordinates.forEach((ring) => ring.forEach(one));
  else if (geometry.type === "MultiPolygon")
    geometry.coordinates.forEach((poly) =>
      poly.forEach((ring) => ring.forEach(one)),
    );
}

function isGeographicWgs84(wkt: string): boolean {
  return /^\s*GEOG(CS|CRS)\b/i.test(wkt) && /WGS[ _]?19?84|4326/i.test(wkt);
}

function applyPrj(
  features: GeoJSON.Feature[],
  prj: string | undefined,
  warns: string[],
): GeoJSON.Feature[] {
  const wkt = prj?.trim();
  if (wkt) {
    if (isGeographicWgs84(wkt)) return features;
    try {
      const conv = proj4(wkt);
      let bad = false;
      for (const feature of features) {
        if (!feature.geometry) continue;
        mapCoords(feature.geometry, ([x, y]) => {
          const [lng, lat] = conv.inverse<XY>([x, y]);
          if (!Number.isFinite(lng) || !Number.isFinite(lat)) bad = true;
          return [lng, lat];
        });
      }
      if (bad) throw new Error("non-finite reprojection result");
      return features;
    } catch {
      warns.push("unusable .prj — refusing to guess the CRS, file dropped");
      return [];
    }
  }
  let lonLatLike = true;
  for (const feature of features) {
    if (!feature.geometry) continue;
    mapCoords(feature.geometry, (pt) => {
      if (Math.abs(pt[0]) > 180 || Math.abs(pt[1]) > 90) lonLatLike = false;
      return pt;
    });
  }
  if (!lonLatLike) {
    warns.push(
      "no .prj and coordinates are outside lon/lat range — refusing to guess, file dropped",
    );
    return [];
  }
  return features;
}

export function readShapefile(parts: {
  shp: Uint8Array;
  dbf?: Uint8Array;
  prj?: string;
}): GeoJSON.Feature[] {
  const warns: string[] = [];
  const geoms = geometries(parts.shp, warns);
  const rows = parts.dbf ? dbfRows(parts.dbf, warns) : [];
  const features: GeoJSON.Feature[] = [];
  geoms.forEach((geometry, rec) => {
    if (!geometry) return;
    features.push({
      type: "Feature",
      geometry,
      properties: rows[rec] ?? {},
    });
  });
  if (!features.length) return done(warns, features);
  return done(warns, applyPrj(features, parts.prj, warns));
}
